/**
 * Générateur — « Connaitre les critères de divisibilité par 3 et par 9. »
 * Attendu ID : 908 | Niveau : 5ème | Domaine : Nombres et calculs / Opérations
 *
 * Mis en conformité avec la spécification 908.json (campagne 5e, 2026-09-23).
 * La spécification est la seule référence ; ce qu'elle ne dit pas est noté
 * dans le rapport audit/5eme/908.md (section « Non tranché »).
 *
 * ─── Plan de cartes ────────────────────────────────────────────────────────
 *
 *   Carte « entretien » — Critères par 2, par 5 et par 10 (niveau 1)
 *     Format : tableau_vf. Trois critères du cycle 3 sur des nombres à trois
 *     chiffres, dont l'implication « divisible par 10 donc divisible par 2 et
 *     par 5 » posée par construction (deux régimes de tirage).
 *
 *   Carte « cocher » — Divisible par 3 ? par 9 ? (niveau 2)
 *     Formats : qcm_multiple (compte annoncé), tableau_vf. Un seul critère par
 *     question, nombres de 4 à 5 chiffres CONSTRUITS par leur décomposition :
 *     divisible par 3 et pas par 9 (3 × p × k, p premier au moins égal à 7,
 *     k non multiple de 3), divisible par 9 (9 × p × k), non divisible (multiple
 *     augmenté d'un reste de 1 à 8), ou non divisible mais de chiffre des
 *     unités trompeur (3, 6 ou 9).
 *
 *   Carte « trois_neuf » — Par 3 seulement, ou aussi par 9 ? (niveau 4)
 *     Formats : tableau colonnesLibres à CATÉGORIES EXCLUSIVES (« par 3
 *     seulement / par 3 et par 9 / ni par 3 ni par 9 »), qcm_multiple (coche
 *     les nombres divisibles par 3 mais pas par 9, compte annoncé).
 *     C'est le cœur de l'objectif : la divisibilité par 9 entraîne celle par 3,
 *     l'inverse est faux. Deux nombres sur cinq sont divisibles par 3 sans
 *     l'être par 9 (la spécification demande de 25 % à 50 %).
 *
 *   Carte « croises » — Divisibles à la fois par… (niveau 3)
 *     Formats : tableau colonnesLibres à catégories exclusives (« par p
 *     seulement / par q seulement / par p et par q / ni l'un ni l'autre »),
 *     qcm_multiple (compte annoncé). Couples et triplets premiers entre eux
 *     pris dans les cinq critères autorisés (2, 3, 5, 9, 10), chacun contenant
 *     le 3 ou le 9. Nombres bâtis exprès (multiples du produit = les « oui »,
 *     multiples d'un seul = les pièges), toujours à trois chiffres au moins.
 *
 *   Carte « completer » — Complète avec un chiffre (niveau 5)
 *     Formats : texte (le PLUS PETIT chiffre qui convient — unicité par la
 *     consigne), qcm_unique (un seul des quatre chiffres proposés convient),
 *     qcm_multiple (TOUS les chiffres qui conviennent, compte annoncé : c'est
 *     là que se voit qu'ils sont plusieurs). Critères 3 et 9 en saisie et en
 *     QCM multiple, 2, 3 et 9 en QCM simple.
 *
 *   Carte « tartelettes » — Des parts toutes identiques (niveau 3)
 *     Format : tableau_vf. Quantités bâties par RÉGIMES de divisibilité
 *     (multiples de 15 / de 10 / de 9 / de 45, cofacteurs contraints) : les
 *     verdicts pour quatre critères tirés parmi 2, 3, 5, 9 et 10 varient d'un
 *     tirage à l'autre, sans motif mémorisable.
 *
 *   Carte « parcours » — Le chemin des multiples (niveau 6)
 *     Format : qcm_unique. Trois chemins de trois nombres à cinq chiffres ; un
 *     seul ne passe que par des multiples du critère testé. Au moins un nombre
 *     du bon chemin a une somme de chiffres supérieure à 18 : il faut réitérer.
 *
 *   Carte « sans_critere » — Quand le critère n'existe pas (niveau 7)
 *     Format : qcm_unique. Diviseur pris dans 7, 11, 13, 17, 19 ; la consigne
 *     annonce que tous les diviseurs n'ont pas de critère.
 *
 * ─── Spécification (908.json) ──────────────────────────────────────────────
 *
 *   Bornes appliquées :
 *     - critères nouveaux : 3 et 9 seulement (`prescrit`) ;
 *     - critères d'entretien : 2, 5 et 10 seulement (`prescrit`) ;
 *     - diviseurs sans critère : 7, 11, 13, 17, 19, dans leur carte annoncée ;
 *     - entier à tester : de 100 à 99 999 (3 chiffres au minimum, 5 au plus) ;
 *     - nombre à compléter : 3 ou 4 chiffres ;
 *     - critères confrontés dans une question : de 1 à 5 ;
 *     - part de nombres divisibles par 3 mais pas par 9 : 1 ligne sur 4 dans le
 *       tableau vrai/faux, 2 nombres sur 5 ou 6 ailleurs (bornes 0,25 à 0,5) ;
 *     - écart au multiple des nombres non divisibles : de 1 à 8, toujours
 *       strictement inférieur au diviseur testé.
 *   Exclusions appliquées : aucun critère par 4, par 6 ni par 15 (le critère
 *     par 6 de la carte « completer » est supprimé) ; aucun entier à moins de
 *     trois chiffres ; un nombre divisible par 3 et pas par 9 est CONSTRUIT
 *     (3 × p × k, p premier au moins égal à 7, k non multiple de 3), jamais
 *     tiré puis vérifié ; aucune proposition qui ne peut jamais être correcte ;
 *     aucun nombre à tester en double dans une même question.
 *   Erreurs visées → mots-clés (codes de `_meta.params.distracteurs[].code`
 *     ou `_meta.params.erreurVisee`) :
 *     1. « Conclure de la divisibilité par 3 à celle par 9 »  → trois_donc_neuf
 *     2. « Somme encore grande, ne pas réitérer »             → somme_non_reiteree
 *     3. « Appliquer le critère au seul chiffre des unités »  → critere_sur_les_unites
 *     4. « Croire qu'un critère existe pour tout diviseur »   → critere_invente
 *     5. « Traiter les critères comme exclusifs »             → criteres_exclusifs
 *     6. « Oublier que 10 entraîne 2 et 5 »                   → dix_donc_deux_et_cinq
 *     7. « Un seul chiffre possible alors que plusieurs… »    → plusieurs_chiffres
 *   Graduation → cartes :
 *     niveau 1 → entretien_tableau_vf ;
 *     niveau 2 → cocher (2 formats) ;
 *     niveau 3 → croises (2 formats), tartelettes_tableau_vf ;
 *     niveau 4 → trois_neuf (2 formats) ;
 *     niveau 5 → completer (3 formats) ;
 *     niveau 6 → parcours_qcm_unique ;
 *     niveau 7 → sans_critere_qcm_unique.
 *   Non tranché (détail dans le rapport) :
 *     - le format « Croiser » du `type_reponse` (un tableau à cocher, un critère
 *       par colonne, PLUSIEURS coches par ligne, une rétroaction par case)
 *       n'existe pas dans l'application : une ligne ne porte qu'une coche. Les
 *       deux tableaux croisés passent donc par des catégories EXCLUSIVES ;
 *     - la troisième option neutre « je ne sais pas » du format « Décider »
 *       n'est pas servie : une proposition jamais correcte serait comptée
 *       fausse par le scoring et ne renseignerait personne ;
 *     - le parcours du niveau 6 se joue, dans la spécification, sur une grille
 *       parcourue case à case ; l'application n'a pas ce format. La carte
 *       propose trois chemins écrits, dont un seul est praticable.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux,
} from '../../../utils';

// ─── Constantes ──────────────────────────────────────────────
const ATTENDU_ID = 908;

const L_ENTRE  = 'Critères par 2, par 5 et par 10';
const L_COCHER = 'Divisible par 3 ? par 9 ?';
const L_TROIS9 = 'Par 3 seulement, ou aussi par 9 ?';
const L_CROISE = 'Divisibles à la fois par…';
const L_COMPL  = 'Complète avec un chiffre';
const L_TARTE  = 'Des parts toutes identiques';
const L_PARC   = 'Le chemin des multiples';
const L_SANS   = 'Quand le critère n\'existe pas';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

/** Paramètres de tirage exposés au harnais et au test de conformité (jamais persistés). */
const avecParams = (payload, params) => {
    payload._meta.params = params;
    return payload;
};

const MOTS_COMPTE = { 2: 'deux', 3: 'trois', 4: 'quatre' };

/** Somme des chiffres. */
const sommeChiffres = (n) => String(n).split('').reduce((s, c) => s + parseInt(c, 10), 0);

const bornes = (nbChiffres) => ({ min: 10 ** (nbChiffres - 1), max: 10 ** nbChiffres - 1 });

/**
 * Diviseurs sans critère simple (spéc. `diviseurs_sans_critere`). Ils servent
 * aussi de facteur imposé dans la construction des nombres « divisibles par 3
 * mais pas par 9 » : le cofacteur ne peut alors pas réintroduire un 3.
 */
const PREMIERS_SANS_CRITERE = [7, 11, 13, 17, 19];

/** n = 3 × p × k, p premier ≥ 7, k non multiple de 3 : divisible par 3, JAMAIS par 9. */
const nombreTroisPasNeuf = (nbChiffres) => {
    const { min, max } = bornes(nbChiffres);
    for (let i = 0; i < 60; i++) {
        const base = 3 * randomChoice(PREMIERS_SANS_CRITERE);
        const k = randInt(Math.ceil(min / base), Math.floor(max / base));
        if (k % 3 === 0) continue;
        const n = base * k;
        if (n < min || n > max) continue;
        return n;
    }
    return [105, 1050, 10500][Math.max(0, Math.min(2, nbChiffres - 3))];
};

/** n = 9 × p × k, p premier ≥ 7 : divisible par 9 (donc par 3). */
const nombreDivisiblePar9 = (nbChiffres) => {
    const { min, max } = bornes(nbChiffres);
    for (let i = 0; i < 60; i++) {
        const base = 9 * randomChoice(PREMIERS_SANS_CRITERE);
        const kMin = Math.ceil(min / base);
        const kMax = Math.floor(max / base);
        if (kMax < kMin) continue;
        const n = base * randInt(kMin, kMax);
        if (n < min || n > max) continue;
        return n;
    }
    return [126, 1260, 12600][Math.max(0, Math.min(2, nbChiffres - 3))];
};

/** Multiple de m augmenté d'un reste de 1 à 8, strictement inférieur à m. */
const nombreNonDivisible = (nbChiffres, m) => {
    const { min, max } = bornes(nbChiffres);
    const ecartMax = Math.min(m - 1, 8);
    const q = randInt(Math.ceil(min / m), Math.floor((max - ecartMax) / m));
    return m * q + randInt(1, ecartMax);
};

/**
 * Non divisible par m, mais dont le chiffre des unités est 3, 6 ou 9.
 * Le chiffre des unités est FIXÉ d'abord et les chiffres de gauche sont tirés
 * ensuite : remplacer le chiffre des unités d'un nombre déjà tiré ne change pas
 * son reste modulo 3, et une fois sur trois aucun remplacement ne convenait.
 */
const nombreUnitesTrompeuses = (nbChiffres, m) => {
    const u = randomChoice([3, 6, 9]);
    const { min, max } = bornes(nbChiffres);
    for (let i = 0; i < 60; i++) {
        const n = randInt(Math.ceil(min / 10), Math.floor(max / 10)) * 10 + u;
        if (n >= min && n <= max && n % m !== 0) return n;
    }
    for (let n = min - (min % 10) + u; n <= max; n += 10) if (n >= min && n % m !== 0) return n;
    return min + u;
};

/**
 * Un nombre distinct de ceux déjà tirés (exclusion « aucun nombre à tester
 * identique à un autre dans une même série »). Le repli ajoute 90, qui
 * préserve le reste modulo 3, modulo 9 ET le chiffre des unités.
 */
const distinct = (fabrique, vus) => {
    let n = fabrique();
    for (let i = 0; i < 30 && vus.has(n); i++) n = fabrique();
    for (let i = 0; i < 20 && vus.has(n) && n + 90 <= 99999; i++) n += 90;
    vus.add(n);
    return n;
};

/** Un nombre dont la somme des chiffres dépasse 18 : le critère doit être réitéré. */
const avecGrosseSomme = (fabrique) => {
    for (let i = 0; i < 40; i++) {
        const n = fabrique();
        if (sommeChiffres(n) > 18) return n;
    }
    return fabrique();
};

// ═══════════════════════════════════════════════════════════
// Carte 1 — Critères par 2, par 5 et par 10 (niveau 1)
// ═══════════════════════════════════════════════════════════

const genEntretienVF = () => {
    // Deux régimes : le nombre est multiple de 10 (les trois critères tombent
    // ensemble), ou il ne vérifie qu'un seul des deux critères de base.
    const parDix = randomChoice([true, false]);
    let principal = 10 * randInt(10, 99);                         // 100 à 990, multiple de 10
    if (!parDix) {
        principal = 2 * randInt(50, 499);                         // pair, jamais multiple de 5
        for (let i = 0; i < 20 && principal % 5 === 0; i++) principal = 2 * randInt(50, 499);
        if (principal % 5 === 0) principal += 2;
    }
    const impairMultipleDe5 = 5 * (2 * randInt(10, 99) + 1);      // multiple de 5, jamais de 2 ni de 10
    const lignes = shuffle([
        { txt: `${principal} est divisible par 2`, v: principal % 2 === 0, code: 'dix_donc_deux_et_cinq' },
        { txt: `${principal} est divisible par 5`, v: principal % 5 === 0, code: 'dix_donc_deux_et_cinq' },
        { txt: `${principal} est divisible par 10`, v: principal % 10 === 0, code: 'dix_donc_deux_et_cinq' },
        { txt: `${impairMultipleDe5} est divisible par 10`, v: false, code: 'dix_donc_deux_et_cinq' },
    ]);
    const texte = '<p>Ces critères ont été vus à l\'école. Pour chaque phrase, ' +
        'indique si elle est vraie ou fausse.</p>';
    const payload = makeVraiFaux(
        lignes.map(l => l.txt), lignes.map(l => (l.v ? 0 : 1)), texte, opts(L_ENTRE),
    );
    return avecParams(payload, {
        niveau: 1, criteres: [2, 5, 10], nombres: [principal, impairMultipleDe5],
        erreurVisee: 'dix_donc_deux_et_cinq',
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 2 — Divisible par 3 ? par 9 ? (niveau 2)
// ═══════════════════════════════════════════════════════════

const genCocherQcmMulti = () => {
    const m = randomChoice([3, 9]);
    const nbChiffres = randInt(4, 5);
    const nbBons = randInt(2, 4);
    const vus = new Set();
    const bons = [];
    const pieges = [];
    if (m === 3) {
        // Deux « divisibles par 3 mais pas par 9 » sur six : 33 % (bornes 25 à 50).
        bons.push(distinct(() => nombreTroisPasNeuf(nbChiffres), vus));
        bons.push(distinct(() => nombreTroisPasNeuf(nbChiffres), vus));
        while (bons.length < nbBons) bons.push(distinct(() => nombreDivisiblePar9(nbChiffres), vus));
        pieges.push(distinct(() => nombreUnitesTrompeuses(nbChiffres, 3), vus));
        while (pieges.length < 6 - nbBons) pieges.push(distinct(() => nombreNonDivisible(nbChiffres, 3), vus));
    } else {
        while (bons.length < nbBons) bons.push(distinct(() => nombreDivisiblePar9(nbChiffres), vus));
        // Les deux pièges qui portent l'objectif : divisibles par 3, pas par 9.
        // Les autres ne sont divisibles NI par 3 ni par 9 : la part de « divisible
        // par 3 mais pas par 9 » reste à 2 sur 6, dans les bornes de la spéc.
        pieges.push(distinct(() => nombreTroisPasNeuf(nbChiffres), vus));
        pieges.push(distinct(() => nombreTroisPasNeuf(nbChiffres), vus));
        if (pieges.length < 6 - nbBons) pieges.push(distinct(() => nombreUnitesTrompeuses(nbChiffres, 3), vus));
        while (pieges.length < 6 - nbBons) pieges.push(distinct(() => nombreNonDivisible(nbChiffres, 3), vus));
    }
    const choix = shuffle([...bons, ...pieges]).map(String);
    const texte = `<p>Coche les <strong>${MOTS_COMPTE[nbBons]}</strong> nombres divisibles ` +
        `par <strong>${m}</strong>. <em>(Utilise le critère : la somme des chiffres.)</em></p>`;
    const payload = makeQcm(choix, bons.map(String), texte, opts(L_COCHER), true);
    return avecParams(payload, {
        niveau: 2, critere: m, bons,
        distracteurs: pieges.map(n => ({
            valeur: String(n),
            code: n % 3 === 0 ? 'trois_donc_neuf'
                : ([3, 6, 9].includes(n % 10) ? 'critere_sur_les_unites' : 'somme_non_reiteree'),
        })),
    });
};

const genCocherVF = () => {
    const m = randomChoice([3, 9]);
    const nbChiffres = randInt(4, 5);
    const vus = new Set();
    // Une ligne sur quatre est « divisible par 3 mais pas par 9 » : 25 %.
    const specimens = [
        { n: distinct(() => nombreTroisPasNeuf(nbChiffres), vus), code: 'trois_donc_neuf' },
        { n: distinct(() => nombreDivisiblePar9(nbChiffres), vus), code: 'trois_donc_neuf' },
        // Ni par 3 ni par 9 : la part de « divisible par 3 mais pas par 9 »
        // reste à 1 ligne sur 4, dans les bornes de la spéc.
        { n: distinct(() => nombreUnitesTrompeuses(nbChiffres, 3), vus), code: 'critere_sur_les_unites' },
        { n: distinct(() => avecGrosseSomme(() => nombreNonDivisible(nbChiffres, 3)), vus), code: 'somme_non_reiteree' },
    ];
    const lignes = shuffle(specimens.map(s => ({
        txt: `${s.n} est divisible par ${m}`, v: s.n % m === 0, code: s.code,
    })));
    const texte = `<p>À l'aide du critère de divisibilité par <strong>${m}</strong>, ` +
        'indique si chaque phrase est vraie ou fausse.</p>';
    const payload = makeVraiFaux(
        lignes.map(l => l.txt), lignes.map(l => (l.v ? 0 : 1)), texte, opts(L_COCHER),
    );
    return avecParams(payload, {
        niveau: 2, critere: m,
        distracteurs: lignes.filter(l => !l.v).map(l => ({ valeur: l.txt, code: l.code })),
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 3 — Par 3 seulement, ou aussi par 9 ? (niveau 4)
// Le cœur de l'objectif : 9 entraîne 3, l'inverse est faux.
// ═══════════════════════════════════════════════════════════

const COLONNES_TROIS_NEUF = [
    'Divisible par 3 seulement',
    'Divisible par 3 et par 9',
    'Ni par 3 ni par 9',
];

const genTroisNeufTableau = () => {
    const nbChiffres = randInt(4, 5);
    const vus = new Set();
    const tirages = shuffle([
        { n: distinct(() => nombreTroisPasNeuf(nbChiffres), vus), col: 0 },
        { n: distinct(() => nombreTroisPasNeuf(nbChiffres), vus), col: 0 },
        { n: distinct(() => nombreDivisiblePar9(nbChiffres), vus), col: 1 },
        { n: distinct(() => nombreDivisiblePar9(nbChiffres), vus), col: 1 },
        { n: distinct(() => nombreUnitesTrompeuses(nbChiffres, 3), vus), col: 2 },
    ]);
    // Catégorie RECALCULÉE sur le nombre : le tableau et sa correction sortent
    // du même calcul (points_de_vigilance 3).
    const categorie = (n) => (n % 9 === 0 ? 1 : (n % 3 === 0 ? 0 : 2));
    const lignes = tirages.map(t => String(t.n));
    const bonnesReponses = tirages.map(t => categorie(t.n));
    const br = lignes.map((l, i) => `[${l}|${COLONNES_TROIS_NEUF[bonnesReponses[i]]}]`).join('');
    const texte = '<p>Pour chaque nombre, coche la colonne qui convient. ' +
        '<em>(Rappel : un nombre divisible par 9 est aussi divisible par 3.)</em></p>';
    const payload = {
        type: 'qcm',
        texte,
        contenu: { choix: [], bonne_reponse: br },
        elements_additionnels: {
            version: 1,
            elements: [{
                type: 'table',
                config: { type: 'colonnesLibres', colonnes: COLONNES_TROIS_NEUF, lignes, bonnesReponses },
            }],
        },
        niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID,
        _meta: { label: L_TROIS9 },
    };
    return avecParams(payload, {
        niveau: 4, criteres: [3, 9], nombres: tirages.map(t => t.n),
        partTroisPasNeuf: bonnesReponses.filter(c => c === 0).length / lignes.length,
        erreurVisee: 'trois_donc_neuf',
    });
};

const genTroisNeufQcmMulti = () => {
    const nbChiffres = randInt(4, 5);
    const nbBons = randInt(2, 3);
    const vus = new Set();
    const bons = [];
    const pieges = [];
    while (bons.length < nbBons) bons.push(distinct(() => nombreTroisPasNeuf(nbChiffres), vus));
    pieges.push(distinct(() => nombreDivisiblePar9(nbChiffres), vus));
    pieges.push(distinct(() => nombreDivisiblePar9(nbChiffres), vus));
    while (pieges.length < 6 - nbBons) pieges.push(distinct(() => nombreUnitesTrompeuses(nbChiffres, 3), vus));
    const choix = shuffle([...bons, ...pieges]).map(String);
    const texte = `<p>Coche les <strong>${MOTS_COMPTE[nbBons]}</strong> nombres qui sont ` +
        'divisibles <strong>par 3 mais pas par 9</strong>.</p>';
    const payload = makeQcm(choix, bons.map(String), texte, opts(L_TROIS9), true);
    return avecParams(payload, {
        niveau: 4, criteres: [3, 9], bons,
        distracteurs: pieges.map(n => ({
            valeur: String(n),
            code: n % 9 === 0 ? 'trois_donc_neuf' : 'critere_sur_les_unites',
        })),
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 4 — Divisibles à la fois par… (niveau 3)
// ═══════════════════════════════════════════════════════════

// Chaque couple contient le 3 ou le 9 (le critère travaillé), croisé avec un
// critère d'entretien du cycle 3. Tous premiers entre eux : les quatre colonnes
// exclusives restent atteignables ({3, 9} est traité par sa propre carte).
const COUPLES_CROISES = [[3, 2], [3, 5], [3, 10], [9, 2], [9, 5], [9, 10]];
// Triplets pour le QCM multiple (premiers entre eux deux à deux).
const TRIPLES_CROISES = [[2, 3, 5], [2, 5, 9]];

/** Multiple de `base` à trois chiffres au moins, jamais multiple de `interdit`. */
const multipleDe = (base, interdit) => {
    for (let i = 0; i < 40; i++) {
        const n = base * randInt(Math.ceil(100 / base), Math.floor(9999 / base));
        if (n % interdit !== 0 && n >= 100) return n;
    }
    for (let n = 100; n <= 9999; n++) if (n % base === 0 && n % interdit !== 0) return n;
    return 100;                                          // inatteignable (bases premières entre elles)
};

/** Multiple de `base` à trois chiffres au moins. */
const multipleAuDessusDeCent = (base) =>
    base * randInt(Math.ceil(100 / base), Math.floor(9999 / base));

const genCroisesTableau = () => {
    const [p, q] = randomChoice(COUPLES_CROISES);
    const produit = p * q;                               // couples premiers entre eux
    const colonnes = [
        `Divisible par ${p} seulement`,
        `Divisible par ${q} seulement`,
        `Divisible par ${p} et par ${q}`,
        `Ni par ${p} ni par ${q}`,
    ];
    const niLunNiLautre = () => {
        for (let i = 0; i < 60; i++) {
            const x = randInt(100, 999);
            if (x % p !== 0 && x % q !== 0) return x;
        }
        for (let x = 101; x <= 999; x++) if (x % p !== 0 && x % q !== 0) return x;
        return 101;
    };
    // Un nombre par catégorie + 2 supplémentaires → 6 lignes, une coche chacune
    const tirages = [
        { n: multipleDe(p, q), col: 0 },
        { n: multipleDe(q, p), col: 1 },
        { n: multipleAuDessusDeCent(produit), col: 2 },
        { n: niLunNiLautre(), col: 3 },
        { n: multipleDe(p, q), col: 0 },
        { n: multipleAuDessusDeCent(produit), col: 2 },
    ];
    // Lignes distinctes — retouche bornée qui PRÉSERVE la catégorie de chaque nombre
    const estDansCategorie = (n, col) => {
        if (col === 0) return n % p === 0 && n % q !== 0;
        if (col === 1) return n % q === 0 && n % p !== 0;
        if (col === 2) return n % produit === 0;
        return n % p !== 0 && n % q !== 0;
    };
    const vus = new Set();
    tirages.forEach(t => {
        for (let i = 0; (vus.has(t.n) || !estDansCategorie(t.n, t.col)) && i < 60; i++) {
            t.n += (t.col === 2 ? produit : t.col === 0 ? p : t.col === 1 ? q : 1);
        }
        vus.add(t.n);
    });
    const ordre = shuffle(tirages);
    const lignes = ordre.map(t => String(t.n));
    const bonnesReponses = ordre.map(t => t.col);
    const br = lignes.map((l, i) => `[${l}|${colonnes[bonnesReponses[i]]}]`).join('');
    const texte = '<p>Pour chaque nombre, coche la colonne qui convient. ' +
        `<em>(Teste les deux critères : par ${p} et par ${q}.)</em></p>`;
    const payload = {
        type: 'qcm',
        texte,
        contenu: { choix: [], bonne_reponse: br },
        elements_additionnels: {
            version: 1,
            elements: [{ type: 'table', config: { type: 'colonnesLibres', colonnes, lignes, bonnesReponses } }],
        },
        niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID,
        _meta: { label: L_CROISE },
    };
    return avecParams(payload, {
        niveau: 3, criteres: [p, q], nombres: ordre.map(t => t.n),
        erreurVisee: 'criteres_exclusifs',
    });
};

const genCroisesQcmMulti = () => {
    const triple = randomChoice([false, false, true]);
    const facteurs = triple ? randomChoice(TRIPLES_CROISES) : randomChoice(COUPLES_CROISES);
    const produit = facteurs.reduce((x, y) => x * y, 1);  // facteurs premiers entre eux
    const nbBons = randInt(2, 3);
    const bons = [];
    const vus = new Set();
    for (let i = 0; i < nbBons; i++) {
        let n = multipleAuDessusDeCent(produit);
        while (vus.has(n)) n += produit;
        vus.add(n);
        bons.push(n);
    }
    const pieges = [];
    for (let i = 0; i < 60 && pieges.length < 6 - nbBons; i++) {
        const f = randomChoice(facteurs);
        const n = multipleDe(f, facteurs.find(x => x !== f));
        if (!vus.has(n) && n % produit !== 0) { vus.add(n); pieges.push(n); }
    }
    while (pieges.length < 6 - nbBons) {                   // repli déterministe
        const f = facteurs[0];
        let n = multipleDe(f, facteurs[1]);
        while (vus.has(n) || n % produit === 0) n += f;
        vus.add(n);
        pieges.push(n);
    }
    const choix = shuffle([...bons, ...pieges]).map(String);
    const liste = facteurs.length === 3
        ? `${facteurs[0]}, ${facteurs[1]} et ${facteurs[2]}`
        : `${facteurs[0]} et ${facteurs[1]}`;
    const texte = `<p>Coche les <strong>${MOTS_COMPTE[nbBons]}</strong> nombres divisibles ` +
        `<strong>à la fois</strong> par ${liste}.</p>`;
    const payload = makeQcm(choix, bons.map(String), texte, opts(L_CROISE), true);
    return avecParams(payload, {
        niveau: 3, criteres: facteurs, bons,
        distracteurs: pieges.map(n => ({ valeur: String(n), code: 'criteres_exclusifs' })),
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 5 — Complète avec un chiffre (niveau 5)
// ═══════════════════════════════════════════════════════════

/** Nombre à trou : chiffres connus + position du trou. Affichage espacé. */
const tirerBaseTrou = (nbChiffres, trouAuBout) => {
    const chiffres = [randInt(1, 9)];
    for (let i = 1; i < nbChiffres; i++) chiffres.push(randInt(0, 9));
    const pos = trouAuBout ? nbChiffres - 1 : randInt(1, nbChiffres - 1);
    return { chiffres, pos, somme: chiffres.reduce((a, b, i) => (i === pos ? a : a + b), 0) };
};

const afficherAvecTrou = (chiffres, pos) =>
    chiffres.map((c, i) => (i === pos ? '◻' : String(c))).join(' ');

const genCompleterSaisie = () => {
    const crit = randomChoice([3, 9]);                    // le critère par 6 est exclu par la spéc.
    const { chiffres, pos, somme } = tirerBaseTrou(randInt(3, 4), false);
    const plusPetit = (crit - (somme % crit)) % crit;
    const texte = `<p>Quel est le <strong>plus petit chiffre</strong> à écrire à la place de ◻ ` +
        `pour que le nombre <strong>${afficherAvecTrou(chiffres, pos)}</strong> soit divisible ` +
        `par <strong>${crit}</strong> ?</p>`;
    const payload = makeSaisie(String(plusPetit), texte, opts(L_COMPL));
    return avecParams(payload, {
        niveau: 5, critere: crit, reponse: plusPetit,
        erreurVisee: 'plusieurs_chiffres',
    });
};

const genCompleterQcm = () => {
    const crit = randomChoice([2, 3, 9]);
    const { chiffres, pos, somme } = tirerBaseTrou(randInt(3, 4), crit === 2);
    let valide;
    let faux;
    if (crit === 2) {
        valide = randomChoice([0, 2, 4, 6, 8]);
        faux = shuffle([1, 3, 5, 7, 9]).slice(0, 3);
    } else if (crit === 3) {
        const x0 = (3 - (somme % 3)) % 3;
        valide = randomChoice([x0, x0 + 3, x0 + 6].filter(x => x <= 9));
        faux = shuffle([0, 1, 2, 3, 4, 5, 6, 7, 8, 9].filter(x => (somme + x) % 3 !== 0)).slice(0, 3);
    } else {
        valide = (9 - (somme % 9)) % 9;
        faux = shuffle([0, 1, 2, 3, 4, 5, 6, 7, 8, 9].filter(x => (somme + x) % 9 !== 0)).slice(0, 3);
    }
    const texte = '<p>Quel chiffre peut-on écrire à la place de ◻ pour que le nombre ' +
        `<strong>${afficherAvecTrou(chiffres, pos)}</strong> soit divisible par <strong>${crit}</strong> ?</p>`;
    const payload = makeQcm([String(valide), ...faux.map(String)], String(valide), texte, opts(L_COMPL));
    return avecParams(payload, {
        niveau: 5, critere: crit, reponse: valide,
        distracteurs: faux.map(x => ({ valeur: String(x), code: 'critere_sur_les_unites' })),
    });
};

/**
 * TOUS les chiffres qui conviennent : c'est là que l'élève voit qu'ils sont
 * plusieurs (erreur visée 7). Critère 3 seulement — avec 9, un seul chiffre
 * convient le plus souvent, et le format perdrait son objet.
 */
const genCompleterQcmMulti = () => {
    const { chiffres, pos, somme } = tirerBaseTrou(randInt(3, 4), false);
    const x0 = (3 - (somme % 3)) % 3;
    const valides = [x0, x0 + 3, x0 + 6, x0 + 9].filter(x => x <= 9);
    const faux = shuffle([0, 1, 2, 3, 4, 5, 6, 7, 8, 9].filter(x => (somme + x) % 3 !== 0))
        .slice(0, 6 - valides.length);
    const choix = shuffle([...valides, ...faux]).map(String);
    const texte = `<p>Coche <strong>tous</strong> les chiffres (il y en a ` +
        `<strong>${MOTS_COMPTE[valides.length]}</strong>) qui peuvent remplacer ◻ pour que ` +
        `<strong>${afficherAvecTrou(chiffres, pos)}</strong> soit divisible par <strong>3</strong>.</p>`;
    const payload = makeQcm(choix, valides.map(String), texte, opts(L_COMPL), true);
    return avecParams(payload, {
        niveau: 5, critere: 3, valides,
        distracteurs: faux.map(x => ({ valeur: String(x), code: 'plusieurs_chiffres' })),
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 6 — Des parts toutes identiques (niveau 3)
// ═══════════════════════════════════════════════════════════

// Les quantités sont à trois chiffres au moins (exclusion de la spéc.) : les
// objets répartis sont donc petits et nombreux, pour que la situation reste
// vraisemblable (défaut vu au dump : 135 morceaux de pommes par tartelette).
const CTX_TARTES = [
    { qui: 'Un artisan', f1: 'perles rouges', f2: 'perles bleues', quoi: 'colliers' },
    { qui: 'Une jardinière', f1: 'graines de radis', f2: 'graines de carotte', quoi: 'sachets' },
    { qui: 'Un animateur', f1: 'gommettes vertes', f2: 'gommettes jaunes', quoi: 'affiches' },
];

/** Régimes de divisibilité — les verdicts pour 2, 3, 5, 9 et 10 varient. */
const REGIMES_TARTES = [
    { fac: 15, contrainte: (k) => k % 3 !== 0 && k % 2 !== 0 },   // 2✗ 3✓ 5✓ 9✗ 10✗
    { fac: 10, contrainte: (k) => k % 3 !== 0 },                  // 2✓ 3✗ 5✓ 9✗ 10✓
    { fac: 9,  contrainte: (k) => k % 5 !== 0 && k % 2 !== 0 },   // 2✗ 3✓ 5✗ 9✓ 10✗
    { fac: 45, contrainte: (k) => k % 2 !== 0 },                  // 2✗ 3✓ 5✓ 9✓ 10✗
];

const tirerTartes = () => {
    const regime = randomChoice(REGIMES_TARTES);
    const kMin = Math.ceil(100 / regime.fac);              // trois chiffres au minimum
    const tirerK = () => {
        for (let i = 0; i < 40; i++) {
            const k = randInt(kMin, kMin + 40);
            if (regime.contrainte(k)) return k;
        }
        for (let k = kMin; k <= kMin + 60; k++) if (regime.contrainte(k)) return k;
        return kMin;
    };
    const ka = tirerK();
    let kb = tirerK();
    for (let i = 0; i < 10 && kb === ka; i++) kb = tirerK();
    if (kb === ka) kb = ka + (regime.contrainte(ka + 2) ? 2 : 6);
    return { a: regime.fac * ka, b: regime.fac * kb };
};

const genTartesVF = () => {
    const { a, b } = tirerTartes();
    const ctx = randomChoice(CTX_TARTES);
    const candidats = shuffle([2, 3, 5, 9, 10]).slice(0, 4);
    const lignesTxt = candidats.map(n => `Faire ${n} ${ctx.quoi} identiques est possible`);
    const bonnes = candidats.map(n => (a % n === 0 && b % n === 0 ? 0 : 1));
    const texte = `<p>${ctx.qui} dispose de <strong>${a} ${ctx.f1}</strong> et de ` +
        `<strong>${b} ${ctx.f2}</strong>, et veut composer des ${ctx.quoi} ` +
        '<strong>identiques</strong> en utilisant tout.</p>' +
        '<p>À l\'aide des critères de divisibilité, indique si chaque affirmation est vraie ou fausse.</p>';
    const payload = makeVraiFaux(lignesTxt, bonnes, texte, opts(L_TARTE));
    return avecParams(payload, {
        niveau: 3, criteres: candidats, quantites: [a, b],
        erreurVisee: 'criteres_exclusifs',
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 7 — Le chemin des multiples (niveau 6)
// ═══════════════════════════════════════════════════════════

const genParcoursQcm = () => {
    const m = randomChoice([3, 9]);
    const vus = new Set();
    const bonNombre = () => (m === 3
        ? (randomChoice([true, false]) ? nombreTroisPasNeuf(5) : nombreDivisiblePar9(5))
        : nombreDivisiblePar9(5));
    const chemin = (fabriques) => fabriques.map(f => distinct(f, vus)).join(', puis ');
    const bon = chemin([
        () => avecGrosseSomme(bonNombre),
        bonNombre,
        bonNombre,
    ]);
    const pieges = [
        {
            txt: chemin([
                bonNombre,
                () => (m === 9 ? nombreTroisPasNeuf(5) : nombreUnitesTrompeuses(5, 3)),
                bonNombre,
            ]),
            code: m === 9 ? 'trois_donc_neuf' : 'critere_sur_les_unites',
        },
        {
            txt: chemin([
                bonNombre,
                bonNombre,
                () => avecGrosseSomme(() => nombreNonDivisible(5, m)),
            ]),
            code: 'somme_non_reiteree',
        },
    ];
    const texte = '<p>Dans ce jeu, on avance de case en case et on ne peut poser le pied que ' +
        `sur un nombre <strong>divisible par ${m}</strong>.</p>` +
        '<p>Quel chemin peut-on suivre <strong>en entier</strong> ?</p>';
    const payload = makeQcm([bon, ...pieges.map(p => p.txt)], bon, texte, opts(L_PARC));
    return avecParams(payload, {
        niveau: 6, critere: m, chemin: bon,
        distracteurs: pieges.map(p => ({ valeur: p.txt, code: p.code })),
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 8 — Quand le critère n'existe pas (niveau 7)
// ═══════════════════════════════════════════════════════════

const genSansCritereQcm = () => {
    const d = randomChoice(PREMIERS_SANS_CRITERE);
    const nbChiffres = randInt(3, 5);
    const { min, max } = bornes(nbChiffres);
    const n = randInt(min, max);
    const bonne = 'Il n\'existe pas de critère simple : il faut poser la division';
    const pieges = [
        { txt: `On additionne les chiffres et on regarde si la somme est divisible par ${d}`, code: 'critere_invente' },
        { txt: 'On regarde seulement le chiffre des unités', code: 'critere_sur_les_unites' },
        { txt: 'On regarde seulement les deux derniers chiffres', code: 'critere_invente' },
    ];
    const texte = '<p><em>Attention : tous les diviseurs n\'ont pas de critère de divisibilité.</em></p>' +
        `<p>Comment décider si <strong>${n}</strong> est divisible par <strong>${d}</strong> ?</p>`;
    const payload = makeQcm([bonne, ...pieges.map(p => p.txt)], bonne, texte, opts(L_SANS));
    return avecParams(payload, {
        niveau: 7, diviseur: d, nombre: n,
        distracteurs: pieges.map(p => ({ valeur: p.txt, code: p.code })),
    });
};

// ─── Registry & Export ───────────────────────────────────────

const GENERATORS = {
    entretien_tableau_vf:    genEntretienVF,
    cocher_qcm_multiple:     genCocherQcmMulti,
    cocher_tableau_vf:       genCocherVF,
    trois_neuf_tableau:      genTroisNeufTableau,
    trois_neuf_qcm_multiple: genTroisNeufQcmMulti,
    croises_tableau:         genCroisesTableau,
    croises_qcm_multiple:    genCroisesQcmMulti,
    completer_texte:         genCompleterSaisie,
    completer_qcm_unique:    genCompleterQcm,
    completer_qcm_multiple:  genCompleterQcmMulti,
    tartelettes_tableau_vf:  genTartesVF,
    parcours_qcm_unique:     genParcoursQcm,
    sans_critere_qcm_unique: genSansCritereQcm,
};

export const CATEGORIES = [
    {
        id: 'entretien', label: L_ENTRE,
        formatsList: [{ key: 'entretien_tableau_vf', label: 'Vrai/Faux' }],
    },
    {
        id: 'cocher', label: L_COCHER,
        formatsList: [
            { key: 'cocher_qcm_multiple', label: 'QCM Multi' },
            { key: 'cocher_tableau_vf',   label: 'Vrai/Faux' },
        ],
    },
    {
        id: 'trois_neuf', label: L_TROIS9,
        formatsList: [
            { key: 'trois_neuf_tableau',      label: 'Tableau' },
            { key: 'trois_neuf_qcm_multiple', label: 'QCM Multi' },
        ],
    },
    {
        id: 'croises', label: L_CROISE,
        formatsList: [
            { key: 'croises_tableau',      label: 'Tableau' },
            { key: 'croises_qcm_multiple', label: 'QCM Multi' },
        ],
    },
    {
        id: 'completer', label: L_COMPL,
        formatsList: [
            { key: 'completer_texte',        label: 'Saisie' },
            { key: 'completer_qcm_unique',   label: 'QCM' },
            { key: 'completer_qcm_multiple', label: 'QCM Multi' },
        ],
    },
    {
        id: 'tartelettes', label: L_TARTE,
        formatsList: [{ key: 'tartelettes_tableau_vf', label: 'Vrai/Faux' }],
    },
    {
        id: 'parcours', label: L_PARC,
        formatsList: [{ key: 'parcours_qcm_unique', label: 'QCM' }],
    },
    {
        id: 'sans_critere', label: L_SANS,
        formatsList: [{ key: 'sans_critere_qcm_unique', label: 'QCM' }],
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(
            (k) => k.startsWith(typeExercice) || k.includes(typeExercice),
        );
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    if (!payload._meta.generatorKey) payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    sommeChiffres, nombreTroisPasNeuf, nombreDivisiblePar9, nombreNonDivisible,
    nombreUnitesTrompeuses, multipleDe, tirerBaseTrou, tirerTartes,
    REGIMES_TARTES, COUPLES_CROISES, TRIPLES_CROISES, PREMIERS_SANS_CRITERE,
};
